/* eslint-disable prettier/prettier */
import React from 'react';
import pageHoc from 'hocs/pageHoc';
import { Text, Flex, Box } from 'rebass/styled-components';
import { InfoBox } from 'components/atoms/InfoBox';
import CompanyNotification from 'components/atoms/CompanyNotification';
import { activeNetworkSelector, hasJobAlertsEnabledSelector } from 'redux/selectors';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import pluralize from 'pluralize';

import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import { ContactsImportBanner } from '../../components/molecules/contactsImportBanner';
import compose from '../../helpers/compose';
import { CalenderDatePicker } from './components/DatePicker';
import { useAnalytics } from './hooks/useAnalytics';
import { useBannerActions } from './hooks/useBannerActions';
import { useDownloadApplicants } from './hooks/useDownloadApplicants';

const pageTitle = 'Job board dashboard';

const Impact = () => {
  const { loadingState, ...analytics } = useAnalytics();
  const banner = useBannerActions();
  const download = useDownloadApplicants();
  const hasJobAlerts = useSelector(hasJobAlertsEnabledSelector);
  const activeNetwork = useSelector(activeNetworkSelector);

  // Redirect to home if the user is not a manager or the network doesn't have a job board
  if (!activeNetwork?.isManager || !activeNetwork?.features?.includes('job_board')) {
    return <Redirect to="/" />;
  }

  const desktopWidth = hasJobAlerts ? '1/3' : '1/2';
  const { contactsImportInProgress = false, matchesBeingGenerated = false, connectTier } = activeNetwork;
  const showContactsOrConnectBanner =
    contactsImportInProgress || matchesBeingGenerated || activeNetwork.isManager || Boolean(connectTier);
  return (
    <MainLayout>
      <MainLayoutHeader title={pageTitle} />
      <Flex flexDirection="column" mx="auto" px="16px" width="100%" maxWidth="800px" sx={{ gap: '64px' }}>
        {showContactsOrConnectBanner && (
          <Flex sx={{ gap: '16px' }} flexDirection="column">
            <ContactsImportBanner network={activeNetwork} />
          </Flex>
        )}
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          <Flex flexWrap="wrap" sx={{ gap: '24px' }} justifyContent="space-between">
            <Text color="text.dark" fontSize="16px" fontWeight="500" lineHeight="20px">
              How is the job board helping our companies?
            </Text>
            <CalenderDatePicker useRangeController setStartDate={analytics.setStartDate} dates={analytics.dates} />
          </Flex>
          <Flex sx={{ gap: '16px', flexDirection: ['column', 'row'] }}>
            <InfoBox
              infoText={
                <Box>
                  <Text mb="8px" fontSize="1">
                    Total number of times pages on your job board were viewed during the selected timeframe.
                  </Text>
                  <Text fontSize="1">
                    This includes repeat views by the same user. For example, a user viewing multiple pages or
                    refreshing a page, each counts as a separate view.
                  </Text>
                </Box>
              }
              width={['100%', desktopWidth]}
              height="100%"
              title="Page views"
              count={analytics.uniqueVisitors.count}
              loading={loadingState.uniqueVisitorsLoading || analytics.uniqueVisitors.error}
            />
            <InfoBox
              infoText={
                <Box>
                  <Text mb="8px" fontSize="1">
                    Number of times users clicked on a job card to view its description.
                  </Text>
                  <Text fontSize="1">
                    The description is shown directly on the job board when available, or users are taken to the
                    original job post when it&apos;s not.
                  </Text>
                </Box>
              }
              width={['100%', desktopWidth]}
              title="Clicks on jobs"
              count={analytics.jobClicks.count}
              loading={loadingState.jobClicksLoading || analytics.jobClicks.error}
            />
            {hasJobAlerts && (
              <InfoBox
                infoText="Number of users who subscribed to receive job alerts from your job board."
                title="New job alerts subscribers"
                width={['100%', desktopWidth]}
                count={analytics.jobAlert.count}
                loading={loadingState.jobAlertLoading || analytics.jobAlert.error}
              />
            )}
          </Flex>
          <Flex sx={{ flexDirection: ['column', 'row'], gap: '16px' }}>
            <InfoBox
              width={['100%', desktopWidth]}
              title="Likely applied"
              sx={{ height: 'inherit' }}
              count={analytics.jobsLikelyApplied.count}
              loading={loadingState.jobsLikelyAppliedLoading || analytics.jobsLikelyApplied.error}
              actionOnClick={analytics.jobsLikelyApplied.count > 0 ? analytics.onDownloadLikelyApplied : null}
              actionText="Export"
              actionLoading={loadingState.downloadLikelyAppliedLoading}
              infoText={
                <Box>
                  <Text mb="8px" fontSize="1">
                    Number of times users clicked the &apos;Apply&apos; button after viewing a job description. This is
                    a strong signal of intent to apply, because they&apos;ve already read the job description.
                  </Text>
                  <Text fontSize="1">
                    Only includes jobs with descriptions displayed on your board (all posted jobs and most extracted
                    jobs).
                  </Text>
                </Box>
              }
            />
            <InfoBox
              width={['100%', desktopWidth]}
              title="Applications"
              sx={{ height: 'inherit' }}
              count={analytics.jobsApplicants.count}
              loading={loadingState.jobsApplicantsLoading || analytics.jobsApplicants.error}
              actionOnClick={analytics.jobsApplicants.count > 0 ? download.downloadApplicants : null}
              actionLoading={download.loading}
              actionText="Export"
              infoText={
                <Box>
                  <Text mb="8px" fontSize="1">
                    Number of applications submitted directly through your job board.
                  </Text>
                  <Text fontSize="1">Applies only to posted jobs using the built-in application method.</Text>
                </Box>
              }
            />
          </Flex>
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          <Text color="text.dark" fontSize="16px" fontWeight="500" lineHeight="20px">
            Network overview
          </Text>
          <Flex sx={{ gap: '16px', flexDirection: ['column', 'row'] }}>
            <InfoBox
              width={['100%', desktopWidth]}
              title="Visible companies"
              count={analytics.networkCounts.companiesCount}
              loading={loadingState.networkCountsLoading || analytics.networkCounts.error}
            />
            <InfoBox
              width={['100%', desktopWidth]}
              title="Visible jobs"
              count={analytics.networkCounts.jobsCount}
              loading={loadingState.networkCountsLoading || analytics.networkCounts.error}
            />
            {hasJobAlerts && (
              <InfoBox
                width={['100%', desktopWidth]}
                title="Job alerts subscribers"
                count={analytics.allJobAlert.count}
                loading={loadingState.allJobAlertLoading}
              />
            )}
          </Flex>
        </Flex>
        {banner.showBanner && banner.suggestedCompaniesMeta.total > 0 && (
          <CompanyNotification
            onClick={banner.onClickBanner}
            onClose={banner.onCloseBanner}
            show={banner.showBanner}
            title={`${banner.suggestedCompaniesMeta.total} new portfolio ${pluralize(
              'company',
              banner.suggestedCompaniesMeta.total,
            )} ${pluralize('is', banner.suggestedCompaniesMeta.total)} ready to be added`}
            description="Add your last investments to your job board to support them hiring."
            buttonText="See suggested companies"
            sx={{
              mt: '40px',
              button: {
                minWidth: '220px',
              },
            }}
          />
        )}
      </Flex>
    </MainLayout>
  );
};

export default compose(pageHoc({ title: pageTitle }))(Impact);
